import React, { useState, useEffect, useCallback, useRef } from 'react';
import { List, X, ArrowRight, CaretDown, CaretRight } from '@phosphor-icons/react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from '@/lib/motion';
import { navLinks as fallbackNavLinks } from '../data/mock';
import { haptic } from '../utils/haptic';
import { cn } from '../lib/utils';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const NestedSubmenu = ({ item, onNavigate }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        onClick={() => item.href && onNavigate(item.href)}
        className="w-full px-4 py-3 text-left transition-all duration-200 group hover:bg-gallery flex items-center justify-between"
        data-testid={`nav-dropdown-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <div>
          <span className="block text-xs font-semibold uppercase tracking-wider text-obsidian group-hover:text-bronze">
            {item.label}
          </span>
          {item.description && (
            <span className="block text-[10px] mt-1 leading-relaxed text-concrete">
              {item.description}
            </span>
          )}
        </div>
        <CaretRight weight="bold" className="w-3 h-3 text-concrete group-hover:text-bronze" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -8 }}
            transition={{ duration: 0.15 }}
            className="absolute right-full top-0 pr-1"
          >
            <div className="bg-white/95 backdrop-blur-xl shadow-2xl border border-black/5 min-w-[280px] p-2">
              {item.children.map((sub) => (
                <button
                  key={sub.label}
                  onClick={() => onNavigate(sub.href)}
                  className="w-full px-4 py-3 text-left transition-all duration-200 group hover:bg-gallery"
                  data-testid={`nav-dropdown-${sub.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <span className="block text-xs font-semibold uppercase tracking-wider text-obsidian group-hover:text-bronze">
                    {sub.label}
                  </span>
                  {sub.description && (
                    <span className="block text-[10px] mt-1 leading-relaxed text-concrete">
                      {sub.description}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const MobileNestedSubmenu = ({ item, onNavigate }) => {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-2.5 px-3 text-white/70 hover:text-white text-sm"
      >
        <span>{item.label}</span>
        <CaretRight weight="bold" className={cn("w-3.5 h-3.5 transition-transform duration-200", open && "rotate-90")} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="pl-4 pb-2 space-y-1">
              {item.href && (
                <button onClick={() => onNavigate(item.href)} className="block w-full text-left py-2 px-3 text-bronze hover:text-white text-sm font-bold">
                  View All {item.label}
                </button>
              )}
              {item.children.map((sub) => (
                <button
                  key={sub.label}
                  onClick={() => onNavigate(sub.href)}
                  className="block w-full text-left py-2 px-3 text-white/60 hover:text-white text-sm"
                >
                  {sub.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileSubmenu, setMobileSubmenu] = useState(null);
  const [onDarkBackground, setOnDarkBackground] = useState(true);
  const [navLinks, setNavLinks] = useState(fallbackNavLinks);
  const navRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch navigation from API
  useEffect(() => {
    axios.get(`${API}/api/navigation`)
      .then(res => {
        if (res.data?.items?.length > 0) {
          setNavLinks(res.data.items);
        }
      })
      .catch(() => {});
  }, []);

  // Smart background detection based on scroll position
  const detectBackgroundColor = useCallback(() => {
    const scrollY = window.scrollY;
    setScrolled(scrollY > 50);
    
    const isHomePage = location.pathname === '/';
    const isStudioPage = location.pathname.startsWith('/studio') || location.pathname.startsWith('/collections');
    
    // Get the hero section height to determine when to switch
    const heroSection = document.querySelector('[data-testid="page-hero"], section');
    const heroHeight = heroSection?.offsetHeight || 500;
    
    if (isHomePage) {
      // Homepage has a tall hero, switch at 80% of hero height
      setOnDarkBackground(scrollY < heroHeight * 0.8);
    } else if (isStudioPage) {
      // Studio pages are mostly dark themed
      setOnDarkBackground(scrollY < heroHeight * 0.9);
    } else {
      // Other pages - use dark background mode while in hero section
      setOnDarkBackground(scrollY < heroHeight * 0.8);
    }
  }, [location.pathname]);

  useEffect(() => {
    // Initial detection
    detectBackgroundColor();
    
    // Throttled scroll handler
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          detectBackgroundColor();
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', detectBackgroundColor, { passive: true });
    
    // Re-detect after page content loads
    const timer = setTimeout(detectBackgroundColor, 100);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', detectBackgroundColor);
      clearTimeout(timer);
    };
  }, [detectBackgroundColor]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleNavigate = (href) => {
    setMobileOpen(false);
    setActiveDropdown(null);
    setMobileSubmenu(null);
    if (href.startsWith('/')) {
      navigate(href);
    } else if (href.startsWith('#')) {
      if (location.pathname !== '/') {
        navigate('/' + href);
      } else {
        document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const isHomePage = location.pathname === '/';
  const isTransparent = isHomePage && !scrolled;
  const textColor = onDarkBackground ? 'text-white' : 'text-obsidian';
  const textMuted = onDarkBackground ? 'text-white/80' : 'text-obsidian/70';

  return (
    <>
      {/* Main Navigation */}
      <motion.nav
        ref={navRef}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={cn(
          'fixed top-0 left-0 right-0 z-50 transition-all duration-500',
          isTransparent 
            ? 'bg-transparent' 
            : onDarkBackground 
              ? 'bg-obsidian border-b border-white/10' 
              : 'bg-white border-b border-black/10'
        )}
        data-testid="main-navbar"
      >
        <div className="max-w-[1600px] mx-auto px-4 sm:px-8 md:px-16 lg:px-24 xl:px-32">
          <div className="flex items-center justify-between h-16 md:h-24 lg:h-32">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex-shrink-0 relative z-10" 
              data-testid="navbar-logo"
              onClick={(e) => {
                if (location.pathname === '/') {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
            >
              <motion.img
                src={onDarkBackground ? "/logo-white.png" : "/logo-black.png"}
                alt="IHG Industry"
                className="h-10 sm:h-14 md:h-20 lg:h-28 xl:h-32 w-auto transition-all duration-500"
                data-testid="navbar-logo-img"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ 
                  duration: 0.8, 
                  ease: [0.22, 1, 0.36, 1],
                  delay: 0.2 
                }}
                whileHover={{ scale: 1.02 }}
              />
            </Link>

            {/* Desktop Navigation - Mega Menu - Hidden below 1400px */}
            <div className="hidden 2xl:flex items-center gap-1">
              {navLinks.filter(link => !link.cta).map((link) => (
                <div 
                  key={link.label} 
                  className="relative"
                  onMouseEnter={() => link.children && setActiveDropdown(link.label)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <button
                    onClick={() => !link.children && handleNavigate(link.href)}
                    className={cn(
                      'px-4 py-2 font-body text-[11px] font-semibold uppercase tracking-[0.2em] transition-colors duration-300 flex items-center gap-1.5',
                      textMuted,
                      onDarkBackground ? 'hover:text-white' : 'hover:text-obsidian',
                      activeDropdown === link.label && (onDarkBackground ? 'text-white' : 'text-obsidian')
                    )}
                    data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.label}
                    {link.children && <CaretDown weight="bold" className="w-3 h-3" />}
                  </button>
                  
                  {/* Mega Menu Dropdown */}
                  <AnimatePresence>
                    {link.children && activeDropdown === link.label && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-0 pt-4"
                      >
                        <div className="bg-white/95 backdrop-blur-xl shadow-2xl border border-black/5 min-w-[320px] p-2">
                          {link.children.map((child, idx) => (
                            child.divider ? (
                              <div key={idx} className="my-2 border-t border-black/10" />
                            ) : child.children ? (
                              <NestedSubmenu key={child.label} item={child} onNavigate={handleNavigate} />
                            ) : (
                              <button
                                key={child.label}
                                onClick={() => handleNavigate(child.href)}
                                className={cn(
                                  "w-full px-4 py-3 text-left transition-all duration-200 group",
                                  child.featured 
                                    ? "bg-obsidian text-white hover:bg-bronze mt-2" 
                                    : "hover:bg-gallery"
                                )}
                                data-testid={`nav-dropdown-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                              >
                                <span className={cn(
                                  "block text-xs font-semibold uppercase tracking-wider",
                                  child.featured ? "text-white" : "text-obsidian group-hover:text-bronze"
                                )}>
                                  {child.label}
                                </span>
                                {child.description && (
                                  <span className={cn(
                                    "block text-[10px] mt-1 leading-relaxed",
                                    child.featured ? "text-white/70" : "text-concrete"
                                  )}>
                                    {child.description}
                                  </span>
                                )}
                              </button>
                            )
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>

            {/* CTA Button + Mobile Toggle */}
            <div className="flex items-center gap-3">
              {/* CTA Button - Collaborate */}
              <motion.button
                onClick={() => handleNavigate('/collaborate')}
                className={cn(
                  'hidden md:inline-flex items-center gap-2 px-5 py-2.5',
                  'font-body text-[10px] font-bold uppercase tracking-[0.2em]',
                  'transition-all duration-300',
                  onDarkBackground 
                    ? 'bg-white text-obsidian hover:bg-bronze' 
                    : 'bg-obsidian text-white hover:bg-bronze'
                )}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid="navbar-cta-button"
              >
                Collaborate
                <ArrowRight weight="bold" className="w-3.5 h-3.5" />
              </motion.button>

              {/* Mobile Menu Toggle - Shows below 1400px */}
              <button
                onClick={() => { haptic('medium'); setMobileOpen(!mobileOpen); }}
                className={cn('2xl:hidden p-2 transition-colors', textColor)}
                aria-label="Toggle menu"
                data-testid="mobile-menu-toggle"
              >
                {mobileOpen ? <X weight="bold" size={24} /> : <List weight="bold" size={24} />}
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 2xl:hidden"
          >
            <div className="absolute inset-0 bg-obsidian" />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
              className="absolute inset-0 pt-20 md:pt-28 overflow-y-auto"
            >
              <div className="px-6 py-8">
                {navLinks.map((link) => (
                  <div key={link.label} className="border-b border-white/10">
                    {link.children ? (
                      <>
                        <button
                          onClick={() => setMobileSubmenu(mobileSubmenu === link.label ? null : link.label)}
                          className="w-full flex items-center justify-between py-4 text-white min-h-[48px]"
                          aria-label={`${link.label} menu`}
                          aria-expanded={mobileSubmenu === link.label}
                        >
                          <span className="font-display text-lg font-semibold uppercase tracking-wider">
                            {link.label}
                          </span>
                          <CaretRight 
                            weight="bold" 
                            className={cn(
                              "w-5 h-5 transition-transform duration-200",
                              mobileSubmenu === link.label && "rotate-90"
                            )} 
                          />
                        </button>
                        <AnimatePresence>
                          {mobileSubmenu === link.label && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="overflow-hidden"
                            >
                              <div className="pb-4 pl-4 space-y-1">
                                {link.children.filter(c => !c.divider).map((child) => (
                                  child.children ? (
                                    <MobileNestedSubmenu key={child.label} item={child} onNavigate={handleNavigate} />
                                  ) : (
                                    <button
                                      key={child.label}
                                      onClick={() => handleNavigate(child.href)}
                                      className={cn(
                                        "block w-full text-left py-2.5 px-3 transition-colors",
                                        child.featured 
                                          ? "bg-bronze text-obsidian font-semibold text-sm uppercase tracking-wider" 
                                          : "text-white/70 hover:text-white text-sm"
                                      )}
                                    >
                                      {child.label}
                                    </button>
                                  )
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </>
                    ) : (
                      <button
                        onClick={() => handleNavigate(link.href)}
                        className={cn(
                          "w-full text-left py-4 font-display text-lg font-semibold uppercase tracking-wider",
                          link.cta ? "text-bronze" : "text-white"
                        )}
                      >
                        {link.label}
                      </button>
                    )}
                  </div>
                ))}

                {/* Mobile CTA */}
                <motion.button
                  onClick={() => handleNavigate('/collaborate')}
                  className="w-full mt-8 py-4 bg-bronze text-obsidian font-body text-sm font-bold uppercase tracking-widest flex items-center justify-center gap-3"
                  whileTap={{ scale: 0.98 }}
                >
                  Start a Project
                  <ArrowRight weight="bold" className="w-4 h-4" />
                </motion.button>

                {/* Contact Info */}
                <div className="mt-12 pt-8 border-t border-white/10">
                  <p className="text-white/40 text-xs uppercase tracking-widest mb-4">Contact</p>
                  <a href="tel:+97142585754" className="block text-white/70 hover:text-white text-sm mb-2">
                    +971 4 258 5754
                  </a>
                  <a href="mailto:info@ihgind.com" className="block text-white/70 hover:text-white text-sm">
                    info@ihgind.com
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
